/**
 * Приборная панель прохода: две орбиты с метками и цифры в середине.
 *
 * Устроено как автомобильный прибор, и не ради вида. У прибора числа читаются
 * в СЕРЕДИНЕ, на своей подложке, а шкала вокруг отвечает на другой вопрос —
 * «много это или мало». Раньше числа лежали поверх дуги и терялись в ней:
 * тонкий светлый текст на цветной кривой не читается ни при какой яркости.
 *
 * Что на что отображено:
 *
 *   внешняя орбита с меткой — чтение;
 *   внутренняя орбита       — отдача;
 *   цифры вдоль шкалы       — сколько это в единицах текущей ступени;
 *   крупное число           — чтение, оно же ответ на «идёт или встало»;
 *   число над ним           — отдача;
 *   дуга слева              — доля совпавшего: сколько НЕ пришлось отправлять;
 *   дуга справа             — сколько из прочитанного легло в хранилище;
 *   лесенка в разрыве шкалы  — на какой ступени стоит шкала: вверх она
 *                             прыгает сразу, вниз сползает.
 *
 * Метка сидит НА орбите и смотрит внутрь, а не поворачивается из центра.
 * Стрелка из центра тем тоньше у основания, чем длиннее, и короткая вторая на
 * её фоне пропадала вовсе. Две одинаковые метки на своих кругах видны всегда и
 * сравниваются напрямую: одна форма, один размер, разный только цвет.
 *
 * Цвет меток взят не из кольца. Под внешней меткой лежит цветной градиент, и
 * любой её оттенок рано или поздно совпадёт с тем, что под ним, — метки в
 * кольце тонули. Белая и голубая не встречаются в градиенте ни в одной точке,
 * а тёмный контур отделяет их от чего угодно снизу.
 *
 * Боковые дуги не украшение: это два вопроса, которые задают, глядя на
 * скорость. «Почему отдача меньше чтения» отвечает левая, «сколько из этого
 * вообще уехало» — правая. Пройденного тут нет намеренно: оно показано полосой
 * рядом, и второй раз не нужно.
 */
import React, { useEffect, useRef, useState } from 'react';
import { bytes } from '../lib/format.ts';

const CX = 75;
const CY = 75;
/** Орбита чтения — внешняя, орбита отдачи — внутренняя. */
const R = 59;
const RS = 48;
/** Разрыв внизу: там, где у прибора провода, у нас ступени шкалы. */
const FROM = -215;
const SPAN = 250;
/** Радиус, на котором стоят цифры шкалы: между внутренней орбитой и числами. */
const RL = 38.5;

const SIDE_R = 67;
const SIDE_SPAN = 62;

/**
 * Ступени шкалы — как передачи в коробке, а не произвольное число.
 *
 * Предел выбирается из этого списка, и потому у него всего одиннадцать
 * состояний вместо бесконечности. Это и даёт смысл лесенке внизу: ступень
 * можно узнать в лицо, а «шкала до 4,3 МБ/с» узнать нельзя.
 *
 * Числа подобраны так, чтобы четверти делились нацело: на шкале стоят 1, 2, 3,
 * 4, а не 1,07 — дробь в подписи читается дольше, чем сама скорость.
 */
const RANKS: { max: number; unit: string; div: number }[] = [
  { max: 400e3, unit: 'КБ/с', div: 1e3 },
  { max: 800e3, unit: 'КБ/с', div: 1e3 },
  { max: 2e6, unit: 'МБ/с', div: 1e6 },
  { max: 4e6, unit: 'МБ/с', div: 1e6 },
  { max: 8e6, unit: 'МБ/с', div: 1e6 },
  { max: 20e6, unit: 'МБ/с', div: 1e6 },
  { max: 40e6, unit: 'МБ/с', div: 1e6 },
  { max: 100e6, unit: 'МБ/с', div: 1e6 },
  { max: 200e6, unit: 'МБ/с', div: 1e6 },
  { max: 400e6, unit: 'МБ/с', div: 1e6 },
  { max: 1000e6, unit: 'МБ/с', div: 1e6 },
];

function polar(deg: number, radius: number): { x: number; y: number } {
  const a = (deg * Math.PI) / 180;
  return { x: CX + radius * Math.cos(a), y: CY + radius * Math.sin(a) };
}

function arc(fromDeg: number, toDeg: number, radius: number): string {
  const a = polar(fromDeg, radius);
  const b = polar(toDeg, radius);
  const large = Math.abs(toDeg - fromDeg) > 180 ? 1 : 0;
  const sweep = toDeg > fromDeg ? 1 : 0;
  return `M ${a.x} ${a.y} A ${radius} ${radius} 0 ${large} ${sweep} ${b.x} ${b.y}`;
}

/** Клин основанием на орбите и остриём к центру. */
function wedge(deg: number, outer: number, inner: number, halfDeg: number): string {
  const a = polar(deg - halfDeg, outer);
  const b = polar(deg + halfDeg, outer);
  const tip = polar(deg, inner);
  return `M ${a.x} ${a.y} L ${b.x} ${b.y} L ${tip.x} ${tip.y} Z`;
}

/** За сколько секунд потолок сползает вдвое, если сверху ничего не пришло. */
const HALF_LIFE = 10;

function rankOf(v: number): number {
  const i = RANKS.findIndex((r) => v <= r.max);
  return i === -1 ? RANKS.length - 1 : i;
}

/**
 * Ступень шкалы: вверх сразу, вниз сползанием.
 *
 * Раньше это был наибольший замер за минуту. Пока всплески редки, оно работает;
 * но на медиатеке крупный файл попадается чаще раза в минуту, окно не успевает
 * опустеть, и шкала навсегда остаётся на пиковой ступени — метка всё время
 * жмётся к нулю, а ступень «только растёт».
 *
 * Теперь потолок ведёт себя как стрелка с успокоителем: мгновенно поднимается
 * до нового пика и сползает вдвое за десять секунд, если сверху ничего не
 * приходит. Ниже текущей скорости он не опускается — иначе метка упёрлась бы в
 * край.
 *
 * Вниз идём по одной ступени и только с запасом: у самой границы ступень
 * мигала бы туда-сюда, а мигающая шкала хуже неточной.
 */
function useRank(read: number, sent: number): number {
  const now = Math.max(read, sent);
  const nowRef = useRef(now);
  nowRef.current = now;
  const level = useRef(0);
  const last = useRef(0);
  const [rank, setRank] = useState(0);

  useEffect(() => {
    if (now <= level.current) return;
    level.current = now;
    setRank((was) => Math.max(was, rankOf(now)));
  }, [now]);

  useEffect(() => {
    const id = setInterval(() => {
      const t = Date.now();
      const dt = last.current === 0 ? 0 : (t - last.current) / 1000;
      last.current = t;
      level.current = Math.max(nowRef.current, level.current * 0.5 ** (dt / HALF_LIFE));
      setRank((was) => (was > 0 && level.current <= RANKS[was - 1]!.max * 0.9 ? was - 1 : was));
    }, 500);
    return () => clearInterval(id);
  }, []);

  return rank;
}

export interface SpeedGaugeProps {
  /** Байт в секунду, прочитано с диска. */
  read: number;
  /** Байт в секунду, ушло в хранилище. */
  sent: number;
  /** Доля совпавшего, 0…1 — левая дуга. `null`, если считать не из чего. */
  matched?: number | null;
  /**
   * Доля прочитанного, что легла в хранилище, 0…1 — правая дуга.
   *
   * Не «пройдено»: пройденное уже показано полосой рядом, и второй раз оно не
   * нужно. А вот «сколько из прочитанного вообще уехало» — вопрос отдельный и
   * без ответа нигде больше.
   */
  stored?: number | null;
  /** Приглушить: работа молчит дольше срока. */
  quiet?: boolean;
  /**
   * Подписи под числами. По умолчанию про проход.
   *
   * У восстановления та же работа с обратным знаком: `read` — скачано из
   * хранилища, `sent` — записано на диск. Прибор годится тот же, а слова
   * «читает» и «отдаёт» на нём означали бы ровно наоборот.
   */
  words?: { big: string; small: string };
  /**
   * Подписи боковых дуг. По умолчанию про проход: совпало и в хранилище.
   *
   * У восстановления по краям другие вопросы — доля файлов и доля байтов, —
   * и подписывать их словами прохода значило бы соврать дважды на одном
   * приборе.
   */
  sideWords?: { left: string; right: string };
}

/**
 * Доля словами. Начатое, но крохотное — это «<1%», а не «0%».
 *
 * Ноль означает «не начиналось», и в начале большой работы честные 0,003%
 * округлялись именно в него: обе дуги показывали ноль, когда файлы уже шли, и
 * выглядело это как сломанный прибор.
 */
function share(v: number): string {
  const pct = v * 100;
  if (pct > 0 && pct < 1) return '<1%';
  if (pct > 99 && pct < 100) return '>99%';
  return `${Math.round(pct)}%`;
}

export function SpeedGauge({
  read, sent, matched, stored, quiet, words, sideWords,
}: SpeedGaugeProps): React.JSX.Element {
  const bigWord = words?.big ?? 'в секунду читает';
  const smallWord = words?.small ?? '/с отдаёт';
  const rank = useRank(read, sent);
  const scale = RANKS[rank]!;
  const at = (v: number): number => FROM + Math.min(1, v / scale.max) * SPAN;

  const readText = bytes(read);
  const sentText = bytes(sent);
  /** Крупное число ужимается по длине: место в круге конечно, а число любое. */
  const bigSize = readText.length <= 6 ? 18 : readText.length <= 8 ? 15 : 12.5;

  /** Подпись деления — без единицы: единица одна на всю шкалу и стоит внизу. */
  const label = (t: number): string => {
    const v = (scale.max / scale.div) * t;
    return v.toLocaleString('ru-RU', { maximumFractionDigits: v < 10 ? 1 : 0, useGrouping: false });
  };

  const marker = (deg: number, cls: string, orbit: number): React.JSX.Element => (
    <path className={cls} d={wedge(deg, orbit + 4, orbit - 5, 4.6)} />
  );

  return (
    <div className={`gauge${quiet ? ' gauge--quiet' : ''}`}>
      {/*
        Поле обрезано по содержимому, а не взято квадратом. Внизу у прибора
        разрыв шкалы, вверху — поля, и квадрат раздувал виджет пустотой: на
        четырёх агентах это лишний экран прокрутки.
      */}
      <svg viewBox="0 9 150 117" className="gauge__svg" role="img"
           aria-label={`${bigWord} ${readText}, ${smallWord} ${sentText},`
             + ` шкала до ${bytes(scale.max)} в секунду`}>
        <defs>
          {/*
            Цвета разные и по тону, и по СВЕТЛОТЕ. Два соседних тона одинаковой
            яркости сливаются, особенно на тёмном: сине-зелёный градиент глаз
            не различал вовсе.
          */}
          <linearGradient id="ring" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="#2f6fdb" />
            <stop offset="35%" stopColor="#19b9a6" />
            <stop offset="70%" stopColor="#e0b420" />
            <stop offset="100%" stopColor="#e2603f" />
          </linearGradient>
        </defs>

        <path className="gauge__track" d={arc(FROM, FROM + SPAN, R)} />
        <path className="gauge__ring" d={arc(FROM, FROM + SPAN, R)} stroke="url(#ring)" />
        {/* Внутренняя орбита — отдача. Своя дорожка нужна затем, чтобы метки не
            перекрывали друг друга, когда скорости совпадают. */}
        <path className="gauge__track gauge__track--inner" d={arc(FROM, FROM + SPAN, RS)} />

        {/*
          Деления с цифрами. Без них шкала отвечает только «много или мало», а
          спросить хочется «сколько именно» — и лезть за этим в середину, где
          стоит текущая скорость, значит потерять место на шкале.
        */}
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const a = FROM + t * SPAN;
          const o = polar(a, R - 3);
          const i = polar(a, R - 5.5);
          const l = polar(a, RL);
          return (
            <React.Fragment key={t}>
              <line className="gauge__tick" x1={o.x} y1={o.y} x2={i.x} y2={i.y} />
              <text className="gauge__label" x={l.x} y={l.y}
                    textAnchor="middle" dominantBaseline="middle">{label(t)}</text>
            </React.Fragment>
          );
        })}

        {/* Боковые дуги по краю: они отвечают на свои вопросы и не должны
            спорить с кольцом скорости за внимание. */}
        {matched != null && (
          <>
            <path className="gauge__side-track" d={arc(150, 150 + SIDE_SPAN, SIDE_R)} />
            <path className="gauge__side gauge__side--matched"
                  d={arc(150, 150 + SIDE_SPAN * Math.min(1, Math.max(0, matched)), SIDE_R)} />
          </>
        )}
        {stored != null && (
          <>
            <path className="gauge__side-track" d={arc(30, 30 - SIDE_SPAN, SIDE_R)} />
            <path className="gauge__side gauge__side--stored"
                  d={arc(30, 30 - SIDE_SPAN * Math.min(1, Math.max(0, stored)), SIDE_R)} />
          </>
        )}

        {marker(at(sent), 'gauge__mark gauge__mark--sent', RS)}
        {marker(at(read), 'gauge__mark gauge__mark--read', R)}

        {/*
          Подложка под числа. Ради неё всё и затевалось: цифры поверх цветной
          дуги не читаются, а на подложке у них своя территория и свой контраст.
        */}
        <circle className="gauge__dial" cx={CX} cy={CY} r={33} />
        <circle className="gauge__dial-edge" cx={CX} cy={CY} r={33} />

        <text className="gauge__small" x={CX} y={CY - 12} textAnchor="middle">
          {sentText}<tspan className="gauge__small-unit">{smallWord}</tspan>
        </text>
        <text className="gauge__big" x={CX} y={CY + 6} textAnchor="middle" fontSize={bigSize}>
          {readText}
        </text>
        <text className="gauge__big-unit" x={CX} y={CY + 17} textAnchor="middle">{bigWord}</text>

        {/*
          Ступень шкалы лесенкой, а не числом. Число внизу спорило с числами на
          самой шкале — их четыре, и пятое, набранное так же, читалось как ещё
          одно деление. Лесенка отвечает на другой вопрос и выглядит по-другому:
          не «сколько», а «в каком мы диапазоне» — и видно, что он переключился,
          не читая ни одной цифры.
        */}
        <g role="img" aria-label={`Ступень шкалы ${rank + 1} из ${RANKS.length}, до ${bytes(scale.max)} в секунду`}>
          <title>шкала до {bytes(scale.max)}/с · ступень {rank + 1} из {RANKS.length} · переключается сама</title>
          {RANKS.map((_, i) => (
            <rect key={i} className={`gauge__rank-seg${i <= rank ? ' is-on' : ''}`}
                  x={53.6 + i * 2.8} y={122 - (2.4 + i * 0.36)}
                  width={1.8} height={2.4 + i * 0.36} rx={0.5} />
          ))}
          <text className="gauge__rank-unit" x={86} y={122}>{scale.unit}</text>
        </g>

        {matched != null && (
          <text className="gauge__side-label gauge__side-label--matched" x={2} y={122}>
            {sideWords?.left ?? 'совпало'} {share(matched)}
          </text>
        )}
        {stored != null && (
          <text className="gauge__side-label gauge__side-label--stored" x={148} y={122} textAnchor="end">
            {sideWords?.right ?? 'в хранилище'} {share(stored)}
          </text>
        )}
      </svg>
    </div>
  );
}
